import { useState, useRef, useCallback, useEffect } from 'react';
import { getScannerSettings } from '../services/settingsService';

export const useCameraScanner = () => {
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [error, setError] = useState<string | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [capturedImages, setCapturedImages] = useState<string[]>([]);

    const stopCamera = useCallback(() => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
    }, [stream]);

    const startCamera = useCallback(async () => {
        if (stream) return;
        setError(null); // Reset error on new attempt
        
        // FIX: Await promise to get scanner settings before use.
        const settings = await getScannerSettings();
        
        try {
            const constraints: MediaStreamConstraints = {
                video: { 
                    facingMode: 'environment',
                    // Použití uživatelsky nastaveného rozlišení.
                    // Pokud je null, použije se výchozí nastavení zařízení.
                    ...(settings.resolution && {
                        width: { ideal: settings.resolution.width },
                        height: { ideal: settings.resolution.height }
                    })
                }
            };
            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
            setStream(mediaStream);
            if (videoRef.current) {
                videoRef.current.srcObject = mediaStream;
                videoRef.current.play().catch(playError => {
                    console.error("Chyba při spouštění přehrávání videa:", playError);
                    setError("Nepodařilo se spustit video. Zkuste to prosím znovu.");
                    stopCamera();
                });
            }
        } catch (err) {
            console.error("Chyba při přístupu ke kameře:", err);
            let errorMessage = "Nepodařilo se získat přístup ke kameře. Zkontrolujte prosím oprávnění v nastavení prohlížeče.";
             if (err instanceof DOMException) {
                if (err.name === 'NotAllowedError') {
                    errorMessage = 'Přístup ke kameře byl zamítnut. Povolte jej prosím v nastavení prohlížeče.';
                } else if (err.name === 'NotFoundError') {
                    errorMessage = 'Nebyla nalezena žádná vhodná kamera. Ujistěte se, že je připojena a funkční.';
                } else if (err.name === 'OverconstrainedError') {
                    // FIX: Awaited settings object is now correctly accessed.
                    errorMessage = `Požadované rozlišení (${settings.resolution?.width}x${settings.resolution?.height}) není podporováno vaší kamerou. Zkuste v nastavení zvolit nižší rozlišení.`;
                }
            }
            setError(errorMessage);
            stopCamera();
        }
    }, [stream, stopCamera]);

    useEffect(() => {
        // Cleanup function to stop the camera when the component unmounts
        return () => {
            stopCamera();
        };
    }, [stopCamera]);

    // FIX: Make function async
    const captureImage = useCallback(async () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const context = canvas.getContext('2d');
            if (context) {
                context.drawImage(video, 0, 0, canvas.width, canvas.height);
                // Použití uživatelsky nastavené kvality JPEG
                // FIX: Await promise to get scanner settings before use.
                const settings = await getScannerSettings();
                const dataUrl = canvas.toDataURL('image/jpeg', settings.jpegQuality);
                setCapturedImages(prev => [...prev, dataUrl]);
            }
        }
    }, []);

    const deleteImage = useCallback((index: number) => {
        setCapturedImages(prev => prev.filter((_, i) => i !== index));
    }, []);
    
    const clearImages = useCallback(() => {
        setCapturedImages([]);
    }, []);

    return {
        videoRef,
        canvasRef,
        stream,
        error,
        capturedImages,
        // FIX: Expose setCapturedImages so parent components can update the state.
        setCapturedImages,
        startCamera,
        stopCamera,
        captureImage,
        deleteImage,
        clearImages,
    };
};
